import React, { useId, useRef, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Product, ProductCardType } from "../../types/ProductType";
import ProductDataModalBody, {
  ProductDataModal,
} from "../Modals/ProductDataModal";
import ProductButtonsOperations from "./ProductButtonsOperations";
import ProductName from "./ProductName";
import CategoryInput from "./CategoryInput";
import PriceInput from "./PriceInput";
import QuantityInput from "./QuantityInput";
import { PiImageSquareBold } from "react-icons/pi";
import { FaPlus } from "react-icons/fa";
import { Branch, Categories } from "../../types/globalTypes";
import { useSelector } from "react-redux";
import toast from "react-hot-toast";
import axios from "axios";
import { ReactCropperElement, Cropper } from "react-cropper";
import { AppConfig } from "@/utils/AppConfig";
import { getMaxImageSize } from "@/utils/helpers";
import { useTranslation } from "react-i18next";
import useLockBodyScroll from "@/hooks/useLockBodyScroll";
import { BiSolidHide } from "react-icons/bi";

const { MAX_IMAGE_SIZE, MAX_IMAGE_SIZE_MB_String } = getMaxImageSize(
  AppConfig.ImagesMaxSizeInMB.products,
);

function ProductCard({
  _id,
  calories,
  salt,
  caffeine,
  categories,
  descriptionAR,
  descriptionEN,
  image,
  nameAR,
  nameEN,
  price,
  quantity,
  addons,
  lowPrice,
  lowPriceEndDate,
  options,
  allCategories,
  allBranches,
  allBranchesAvailable,
  branches,
  setNeedRefetch,
  productCost,
  hidden
}: ProductCardType & {
  allCategories: Categories;
  setNeedRefetch: React.Dispatch<React.SetStateAction<boolean>>;
  allBranches: Branch[];
}) {
  // Original data before change
  const [originalPhoto, setOriginalPhoto] = useState<string | undefined>(
    undefined,
  );
  const [originalFile, setOriginalFile] = useState<File | null>(null);
  //cropper data
  const [cropedOpen, setCropedOpen] = useState(false);
  const cropperRef = useRef<ReactCropperElement>(null);
  useLockBodyScroll(cropedOpen);
  // other logic
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const [file, setFile] = useState<File | null>(null);
  const [photo, setPhoto] = useState<string | undefined>(undefined);
  const FormId = useId();
  const token = useSelector(
    (state: { auth: { token: string } }) => state.auth.token,
  );

  const [isSending, setIsSending] = useState(false);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];

    if (selectedFile && selectedFile?.size > MAX_IMAGE_SIZE) {
      const maxSize =
        i18n.language === "ar"
          ? MAX_IMAGE_SIZE_MB_String.ar
          : MAX_IMAGE_SIZE_MB_String.en;

      toast.error(t("productCard.fileSizeExceeded", { maxSize }) as string);
      e.target.value = "";
      return;
    }

    if (selectedFile) {
      setOriginalPhoto(photo);
      setOriginalFile(file);

      // Set the new file and photo
      setFile(selectedFile);
      setPhoto(URL.createObjectURL(selectedFile));
      toggleCropedOpen(); // Open the cropper modal
    }
    e.target.value = ""; // Reset the file input
  };

  const onCloseCropper = () => {
    // Reset to the original photo and file
    setPhoto(originalPhoto);
    setFile(originalFile);
    formik.setFieldValue("image", originalFile); // Reset Formik value

    // Close the cropper modal
    setCropedOpen(false);

    // Clear the original state
    setOriginalPhoto(undefined);
    setOriginalFile(null);
  };

  const toggleCropedOpen = () => {
    setCropedOpen((prev) => !prev);
  };

  // Function to handle cropping

  const onCrop = () => {
    if (cropperRef.current) {
      const cropper = cropperRef.current.cropper;
      const canvas = cropper.getCroppedCanvas({ width: 640, height: 640 }); // adjust final size

      // Convert the canvas to a Blob
      if (canvas) {
        canvas?.toBlob(
          (blob) => {
            if (blob) {
              // Convert Blob to File
              const croppedFile = new File([blob], "cropped-image.webp", {
                type: "image/webp",
              });

              setFile(croppedFile);

              formik.setFieldValue("image", croppedFile);

              setPhoto(URL.createObjectURL(blob));

              toggleCropedOpen();
            }
          },
          "image/webp",
          0.6,
        ); // Specify the image format (e.g., webp) & Quality 60%
      }
    }
  };

  const validationSchema = Yup.object({
    nameAR: Yup.string(),
    nameEN: Yup.string().test(
      "at-least-one-lang-name",
      t("productCard.nameRequired") as string,
      function (value) {
        const { nameAR } = this.parent;
        return Boolean(value?.trim() || nameAR?.trim());
      },
    ),
    price: Yup.number()
      .positive(
        t("productCard.pricePositive") as string,
      )
      .required(t("productCard.priceRequired") as string),
    quantity: Yup.string()
      .min(
        0,
        t("productCard.quantityMin") as string,
      )
      .required(t("productCard.quantityReq") as string),
    categories: Yup.array().min(
      1,
      t("productCard.categoryRequired") as string,
    ),
  });

  const handleSubmitProductDataModal = (
    modalValues: Pick<
      Product,
      | "addons"
      | "options"
      | "lowPrice"
      | "lowPriceEndDate"
      | "calories"
      | "salt"
      | "caffeine"
      | "descriptionAR"
      | "descriptionEN"
      | "productCost"
    >,
  ) => {
    formik.setValues({ ...formik.values, ...modalValues });
  };

  const formik = useFormik({
    initialValues: {
      nameAR,
      nameEN,
      categories: categories.map((category) => category.id),
      price,
      quantity,
      image,
      // Product Modal Data
      productCost,
      descriptionAR,
      descriptionEN,
      calories,
      salt,
      caffeine,
      lowPrice,
      lowPriceEndDate,
      addons,
      options,
      branches,
      allBranchesAvailable,
    },
    validationSchema,
    onSubmit: (values) => {
      const BASE_URL = import.meta.env.VITE_BASE_URL;
      const url = `${BASE_URL}/api/dashboard/products/${_id}?locale=${lang}`;

      const branchesIds = values.branches?.map((branch) => branch.id);

      const IsUserSelectAllBranches =
        branchesIds?.length === 0 && allBranches?.length === 0
          ? false
          : branchesIds?.length === allBranches?.length;

      const AddonsWithConvertZeroPriceToUndefined = values?.addons?.map(
        (addon) => ({
          ...addon,
          price: addon.price === 0 ? undefined : addon.price,
        }),
      );

      const OptionsWithConvertZeroPriceToUndefined = values?.options?.map(
        (option) => ({
          ...option,
          options: option?.options?.map((option) => ({
            ...option,
            price: option?.price === 0 ? undefined : option?.price,
          })),
        }),
      );

      const editedProduct = {
        nameAR: values.nameAR || "",
        nameEN: values.nameEN || "",
        categories:
          values.categories.length > 0 ? values.categories : undefined,
        price: values.price,
        quantity: values.quantity,
        descriptionAR: values.descriptionAR || "",
        descriptionEN: values.descriptionEN || "",

        productCost: values.productCost ? values.productCost : "",
        calories: values.calories ? values.calories : "",
        salt: values.salt ? values.salt : "",
        caffeine: values.caffeine ? values.caffeine : "",
        lowPrice: values.lowPrice ? values.lowPrice : "",
        lowPriceEndDate: values.lowPriceEndDate ? values.lowPriceEndDate : "",
        branches: branchesIds?.length > 0 ? branchesIds : undefined,
        allBranchesAvailable: IsUserSelectAllBranches,
        addons:
          values.addons && values.addons?.length > 0
            ? AddonsWithConvertZeroPriceToUndefined
            : "",
        options:
          values.options && values.options?.length > 0
            ? OptionsWithConvertZeroPriceToUndefined
            : "",
        image: file || image,
      };

      const editProduct = async () => {
        const loadingToast = toast.loading(
          t("productCard.editing") as string,
        );
        setIsSending(true);
        try {
          const response = await axios.put(url, editedProduct, {
            headers: {
              Authorization: token,
              "Content-Type": "multipart/form-data",
            },
          });

          if (!response.data.success) {
            toast.error(response.data.error);
            return;
          }

          toast.success(
            (t("productCard.editSuccess") as string)
          )

          setNeedRefetch(true);
        } catch (error: unknown) {
          if (axios.isAxiosError(error)) {
            const message =
              error.response?.data?.error ||
              (t("productCard.editFailed") as string);
            toast.error(message);
          } else {
            toast.error(
              t("productCard.unexpectedError") as string,
            );
            toast.error(
              t("productCard.unexpectedError") as string,
            );
          }
        } finally {
          toast.dismiss(loadingToast);
          setIsSending(false);
        }
      };

      editProduct();
    },
  });

  return (
    <>
      {cropedOpen && (
        <div className="fixed left-0 top-0 z-[99999] flex h-full w-full flex-col items-center justify-center bg-black bg-opacity-50 px-5">
          <div className="flex w-full flex-col items-center gap-5 rounded-md bg-white px-4 py-8 md:w-[60%]">
            {photo && (
              <Cropper
                src={photo || ""}
                style={{ height: 500, width: "100%" }}
                aspectRatio={640 / 640}
                viewMode={1}
                zoomable={true}
                scalable={false}
                cropBoxResizable={false}
                cropBoxMovable={true}
                autoCropArea={1}
                ref={cropperRef}
              />
            )}
            <div className="text-center">
              {t("productCard.imageSizeHint")}
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => {
                  onCrop();
                }}
                className="mt-2 rounded-md bg-sidebar px-5 py-2 text-base font-medium text-skyblue"
              >
                {t("productCard.save")}
              </button>
              <button
                type="button"
                className="mt-2 rounded-md bg-error px-5 py-2 text-base font-medium text-white"
                onClick={onCloseCropper}
              >
                {t("productCard.close")}
              </button>
            </div>
          </div>
        </div>
      )}
      <form
        id={`${FormId}-form`}
        onSubmit={formik.handleSubmit}
        className="w-full rounded-lg bg-white relative"
      >

        <div className="flex flex-col gap-2 rounded-lg border border-l-gray-200 border-r-gray-200 md:border-b-transparent">
        {/* Adjust Small screans */}

        {/* hidden tooltip & icon */}
        {hidden ?<div className="md:absolute m-2 md:m-0 mb-0 relative top-[1px] md:top-2 md:end-2 end-[1px] group flex justify-end">
          <span className=" text-center  z-10 bg-gray-300 p-2 rounded-full w-full md:fit block">
            <BiSolidHide className="text-xl hidden md:block" />
            <span className="md:hidden block font-semibold text-sm select-none">{t("productCard.This_product_is_hidden_from_the_menu")}</span>
          </span>

          {/* Tooltip */}
          <p className="absolute top-[-30px] md:top-[-2rem] md:end-2 end-[1px] bg-gray-300 font-semibold text-black text-sm px-2 py-1 rounded opacity-0 md:group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-20">
            {t("productCard.This_product_is_hidden_from_the_menu")}
          </p>
        </div> : null}


          <div className={`flex flex-row gap-2 pb-1 ps-2 ${hidden ?"pt-0":"pt-2"} md:flex-col md:pb-0 md:ps-0 md:pt-0`}>
            {/* Start Form section one */}

            {/* Start Product Img */}
            <div className="relative aspect-square min-w-[105px] max-w-[105px] overflow-hidden rounded-lg md:w-full md:min-w-full md:max-w-full md:rounded-none md:rounded-t-lg">
              <img
                className="size-full object-cover"
                src={
                  photo ||
                  image ||
                  "/images/placeholders/placeholderProductImage.webp"
                }
                alt="Product-Img"
              />
              <div>
                <input
                  type="file"
                  id={`${FormId}-file-input`}
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />
                <button
                  type="button"
                  className="absolute start-2 top-2 flex items-center gap-2 rounded-full bg-gray-100 text-sm text-sidebar md:bottom-3 md:end-3 md:start-[auto] md:top-[auto] md:px-3 md:py-1"
                  onClick={() =>
                    document.getElementById(`${FormId}-file-input`)?.click()
                  }
                >
                  {/* md screens */}
                  <span className="hidden items-center gap-2 md:flex">
                    <PiImageSquareBold
                      className={`${t("", "mb-[2px]")}`}
                      size={18}
                    />
                    <span>{t("productCard.changePhoto")}</span>
                  </span>

                  {/* sm screans */}
                  <span className="flex size-[25px] items-center justify-center gap-2 rounded-full bg-sidebar text-skyblue md:hidden">
                    <FaPlus size={13} />
                  </span>
                </button>
              </div>
            </div>

            {/* Start Products form */}

            <div className="flex size-full flex-col gap-2 px-2">
              <ProductName
                productNames={{
                  ar: formik.values.nameAR,
                  en: formik.values.nameEN,
                }}
                onChange={(newValues) => {
                  formik.setFieldValue("nameAR", newValues.ar);
                  formik.setFieldValue("nameEN", newValues.en);
                }}
              />
              {formik.touched.nameAR && formik.errors.nameAR && (
                <div className="text-xs text-red-500">
                  {formik.errors.nameAR}
                </div>
              )}

              {formik.touched.nameEN && formik.errors.nameEN && (
                <div className="text-xs text-red-500">
                  {formik.errors.nameEN}
                </div>
              )}

              <PriceInput
                value={formik.values.price}
                onChange={(value) => formik.setFieldValue("price", value)}
              />
              {formik.touched.price && formik.errors.price && (
                <div className="text-xs text-red-500">
                  {formik.errors.price}
                </div>
              )}

              {/* End form Section One */}
            </div>

            {/* Start form Section two */}
          </div>
          <div className="flex flex-col gap-2 p-2 pb-0 pt-0">
            <QuantityInput
              quantity={formik.values.quantity}
              onChange={(value) => formik.setFieldValue("quantity", value)}
            />
            {formik.touched.quantity && formik.errors.quantity && (
              <div className="text-xs text-red-500">
                {formik.errors.quantity}
              </div>
            )}

            <CategoryInput
              allUserCategories={allCategories}
              userPrevSelectedCategories={categories}
              placeholder={t("productCard.categories") as string}
              onCategoryChange={(selectedCategories) =>
                formik.setFieldValue("categories", selectedCategories)
              }
              error={
                formik.touched.categories && formik.errors.categories
                  ? Array.isArray(formik.errors.categories)
                    ? formik.errors.categories.join(", ")
                    : formik.errors.categories
                  : undefined
              }
            />
          </div>

          {/* Start Products Buttons */}
          <ProductButtonsOperations
            setNeedRefetch={setNeedRefetch}
            FormId={FormId}
            nameAR={nameAR}
            nameEN={nameEN}
            _id={_id}
            isSending={isSending}
            hidden={hidden}
          />
        </div>
        {/* End Products Buttons */}
      </form>

      {/* Product Data Modal */}
      <ProductDataModal
        _id={_id}
        FormId={FormId}
        nameAR={nameAR}
        nameEN={nameEN}
      >
        {/* Modal Body */}
        <ProductDataModalBody
          onSubmitProductDataModal={handleSubmitProductDataModal}
          calories={formik.values.calories}
          salt={formik.values.salt}
          caffeine={formik.values.caffeine}
          descriptionAR={formik.values.descriptionAR}
          descriptionEN={formik.values.descriptionEN}
          lowPrice={formik.values.lowPrice}
          lowPriceEndDate={formik.values.lowPriceEndDate}
          addons={formik.values.addons}
          options={formik.values.options}
          allBranches={allBranches}
          branches={formik.values.branches}
          productCost={formik.values.productCost}
          FormId={FormId}
        />
      </ProductDataModal>
    </>
  );
}

export default ProductCard;
